// ============================================================================
// CardShell — shared chrome for Flow chat cards
// 5% white glass surface, 1px hairline border, radius 12, max-width 520;
// header row: kind label (IBM Plex Mono 9px uppercase, dim) + optional
// meanBlue action link right-aligned.
// ============================================================================

import type { ReactNode } from 'react'
import { Box, Typography, type Theme } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { beoflowColors, typography, spacing } from '@/theme/muiTheme'

// The card frames were drawn on the dark canvas; in light mode the same
// white-alpha values vanish (the kind label was invisible), so light falls
// back to the theme's paper, divider and secondary text.
const isDark = (theme: Theme) => theme.palette.mode === 'dark'

/** Card surface per the approved design frames. */
export const cardChromeSx = {
  backgroundColor: (theme: Theme) => (isDark(theme) ? 'rgba(248, 246, 244, 0.05)' : theme.palette.background.paper),
  border: (theme: Theme) => `1px solid ${isDark(theme) ? 'rgba(248, 246, 244, 0.14)' : theme.palette.divider}`,
  borderRadius: '12px',
  maxWidth: 520,
  overflow: 'hidden',
} as const

/** Kind label: IBM Plex Mono 9px uppercase, letterspaced, dim. */
export const kindLabelSx = {
  fontFamily: typography.fontFamilyMonospace,
  fontSize: '9px',
  fontWeight: typography.fontWeightMedium,
  textTransform: 'uppercase',
  letterSpacing: '1.2px',
  color: (theme: Theme) => (isDark(theme) ? 'rgba(248, 246, 244, 0.38)' : theme.palette.text.secondary),
  lineHeight: 1.4,
} as const

/** Hairline that separates a card's header row from its body (design frames). */
export const headerDividerSx = {
  borderBottom: (theme: Theme) => `1px solid ${isDark(theme) ? 'rgba(248, 246, 244, 0.09)' : theme.palette.divider}`,
} as const

/** Small mono meta text (10px) used on data rows / footers. */
export const monoMetaSx = {
  fontFamily: typography.fontFamilyMonospace,
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'text.secondary',
  lineHeight: 1.4,
} as const

/** Right-aligned meanBlue action link (mono, quiet). */
export const actionLinkSx = {
  fontFamily: typography.fontFamilyMonospace,
  fontSize: '10px',
  fontWeight: typography.fontWeightMedium,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: beoflowColors.meanBlue,
  textDecoration: 'none',
  cursor: 'pointer',
  flexShrink: 0,
  '&:hover': { textDecoration: 'underline' },
} as const

export interface CardAction {
  label: string
  /** Internal app route (react-router). */
  to?: string
  onClick?: () => void
}

export function CardActionLink({ action }: { action: CardAction }) {
  if (action.to) {
    return (
      <Typography component={RouterLink} to={action.to} sx={actionLinkSx}>
        {action.label}
      </Typography>
    )
  }
  return (
    <Typography component="span" onClick={action.onClick} sx={actionLinkSx}>
      {action.label}
    </Typography>
  )
}

/** Status/confirmation ring — a circular outline with an optional centered
 *  mono glyph (✓ / − / !), per the receipt anatomy in the design frames. */
export function StatusRing({ color, size = 16, glyph }: { color: string; size?: number; glyph?: string }) {
  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `1px solid ${color}`,
        color,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: typography.fontFamilyMonospace,
        fontSize: `${Math.round(size * 0.6)}px`,
        lineHeight: 1,
      }}
    >
      {glyph ?? ''}
    </Box>
  )
}

interface CardShellProps {
  /** Header kind label, e.g. 'WORKOUT', 'INSIGHT', 'MEMORY'. */
  kindLabel: string
  /** Accent for the kind label — used when the border encodes a category
   *  (sport), so the accent repeats on the label per the design system rule. */
  kindColor?: string
  action?: CardAction
  /** Override the hairline border (e.g. sport-colored WorkoutCard border). */
  borderColor?: string
  children: ReactNode
  onClick?: () => void
  /** Card manages its own body padding (full-bleed rows, e.g. workout blocks). */
  disableBodyPadding?: boolean
}

export default function CardShell({
  kindLabel,
  kindColor,
  action,
  borderColor,
  children,
  onClick,
  disableBodyPadding,
}: CardShellProps) {
  return (
    <Box
      onClick={onClick}
      sx={{
        ...cardChromeSx,
        ...(borderColor ? { border: `1px solid ${borderColor}` } : {}),
        my: spacing.xs,
      }}
    >
      {/* Header row, separated from the body by a hairline (design frames). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          px: spacing.md,
          py: '9px',
          ...headerDividerSx,
        }}
      >
        <Typography sx={{ ...kindLabelSx, ...(kindColor ? { color: kindColor } : {}), flex: 1, minWidth: 0 }} noWrap>
          {kindLabel}
        </Typography>
        {action && <CardActionLink action={action} />}
      </Box>
      <Box sx={disableBodyPadding ? undefined : { px: spacing.md, py: spacing.sm }}>{children}</Box>
    </Box>
  )
}
